// Heads-ups: patterns in how *we're* looking after him that work against the
// five goals (well-behaved, no barking, comfortable, sociable, calm) — spotted
// early, said plainly, with the one change that fixes it. Pure.
//
// These are about our habits, not his: carrying him out late after naps,
// keeping him up too long, going quiet on socialising in the window, never
// practising alone time, telling him off. They only speak when the logs are
// recent enough to trust, and each needs a few examples, not one bad moment.

import type { GoalId } from '../content/goals.ts';
import { FIGURES, cite } from '../content/sources.ts';
import type { SourceRef } from '../content/types.ts';
import { aloneAlertMinutes, awakeWindowMinutes, toiletIntervalMinutes } from './age.ts';
import { LATE_DINNER } from './feeding.ts';
import { awakeStretches, sleepIntervals, type NightWindow } from './sleep.ts';
import { DAY, HOUR, MINUTE, atClock, dayStart, formatDate, formatTime, inClockWindow } from './time.ts';
import type { LogEvent, Millis } from './types.ts';

export interface HeadsUp {
  id: string;
  /** high: changing this now matters; medium: worth a look. */
  level: 'high' | 'medium';
  /** What we're doing ("Taking him out late after naps"). */
  title: string;
  /** The examples behind it. */
  evidence: string;
  /** Why it matters, in terms of the goals. */
  why: string;
  /** The change to make. */
  fix: string;
  goals: GoalId[];
  sources?: SourceRef[];
  route?: string;
  score: number;
}

export interface HeadsUpInput {
  events: LogEvent[];
  now: Millis;
  ageWeeks: number;
  night: NightWindow;
  /** When he came home (nothing is judged before this). */
  homeAt?: Millis;
}

const isToilet = (e: LogEvent): e is LogEvent<'wee' | 'poo'> => e.type === 'wee' || e.type === 'poo';
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Things said to him that sound like telling off — punishment raises stress and slows learning. */
const TELLING_OFF = /\b(told (him )?off|telling (him )?off|shout(ed|ing)?( at him)?|yell(ed|ing)?( at him)?|smack(ed)?|scold(ed)?|punish(ed)?|rubbed his nose|tapped (him|his nose)|alpha roll|scruff(ed)?|pinned him|bopped)\b/i;

export function headsUps(x: HeadsUpInput): HeadsUp[] {
  const { events, now, ageWeeks, night } = x;
  const since = (ms: Millis) => Math.max(now - ms, x.homeAt ?? 0);
  const recent = events.filter((e) => e.at <= now && e.at > since(7 * DAY) && e.type !== 'tracker' && e.source !== 'automation');
  // Quiet when logging has lapsed: no judging a day nobody logged.
  if (!recent.some((e) => e.at > now - 18 * HOUR)) return [];
  const daysLogged = new Set(recent.map((e) => new Date(e.at).toDateString())).size;
  const out: HeadsUp[] = [];
  const add = (h: HeadsUp) => out.push(h);
  const last = (ms: Millis) => recent.filter((e) => e.at > since(ms));

  // 1. Late out after naps: he should be outside within a few minutes of waking.
  {
    const from = since(3 * DAY);
    const wakes = sleepIntervals(events, from, now, now)
      .map(([, end]) => end)
      .filter((end) => end < now - 20 * MINUTE && !inClockWindow(end, night.start, night.end));
    let late = 0;
    let accidents = 0;
    for (const woke of wakes) {
      const first = events.filter(isToilet).filter((e) => e.at >= woke - 2 * MINUTE && e.at <= woke + 45 * MINUTE).sort((a, b) => a.at - b.at)[0];
      if (!first) continue;
      if (first.data.where === 'inside') {
        accidents++;
        late++;
      } else if (first.at - woke > 10 * MINUTE) late++;
    }
    if (wakes.length >= 4 && late >= 3 && late / wakes.length >= 0.4)
      add({
        id: 'late-after-nap',
        level: accidents >= 2 ? 'high' : 'medium',
        title: 'Taking him out late after naps',
        evidence: `${late} of the last ${wakes.length} wake-ups: no wee outside within 10 minutes${accidents ? ` (${plural(accidents, 'accident')} indoors instead)` : ''}.`,
        why: 'Puppies nearly always need to go the moment they wake. Each accident indoors teaches that inside is fine, which slows house training.',
        fix: 'Shoes by the door. The second he stirs, carry him straight down before you do anything else, cue word, reward.',
        goals: ['manners', 'comfortable'],
        sources: [...FIGURES.toiletInterval.sources],
        route: '/insights',
        score: 90 + accidents * 3,
      });
  }

  // 2. Missing the after-meal trip.
  {
    const meals = last(3 * DAY).filter((e) => e.type === 'meal' && e.at < now - 40 * MINUTE && !inClockWindow(e.at, night.start, night.end));
    const missed = meals.filter((m) => !events.some((e) => isToilet(e) && e.at > m.at && e.at <= m.at + 40 * MINUTE));
    const accidents = meals.filter((m) => events.some((e) => isToilet(e) && e.data.where === 'inside' && e.at > m.at && e.at <= m.at + 40 * MINUTE));
    if (meals.length >= 4 && missed.length + accidents.length >= 3)
      add({
        id: 'after-meal',
        level: accidents.length >= 2 ? 'high' : 'medium',
        title: 'Skipping the trip out after meals',
        evidence: `${missed.length + accidents.length} of the last ${meals.length} meals had no trip outside within 40 minutes${accidents.length ? ` (${plural(accidents.length, 'accident')})` : ''}.`,
        why: 'Eating gets everything moving. Most puppies need a wee, and often a poo, 5–30 minutes after a meal.',
        fix: 'Set a 15-minute timer when you put the bowl down, then straight out.',
        goals: ['manners'],
        sources: [...FIGURES.toiletInterval.sources],
        route: '/food',
        score: 80 + accidents.length * 3,
      });
  }

  // 3. Keeping him up too long between naps.
  {
    const window = awakeWindowMinutes(ageWeeks);
    const stretches = awakeStretches(events, since(3 * DAY), now, night);
    // Half as long again as his limit: a few minutes over is normal life, not a pattern.
    const over = Math.round(window * 1.5);
    const long = stretches.filter((m) => m > over);
    const spells = last(3 * DAY).filter((e) => e.type === 'behaviour' && ['bitey', 'zoomies', 'wont-settle', 'jumping'].includes(e.data.kind)).length;
    if (long.length >= 3 && long.length / Math.max(1, stretches.length) >= 0.3)
      add({
        id: 'overtired',
        level: spells >= 3 ? 'high' : 'medium',
        title: 'Keeping him up too long between naps',
        evidence: `${long.length} of ${stretches.length} awake stretches lately ran over ${over} minutes (about ${window} is his limit at ${Math.floor(ageWeeks)} weeks)${spells ? `, and ${plural(spells, 'wild or bitey spell')} logged` : ''}.`,
        why: 'An overtired puppy can’t settle: the biting, zoomies and barking at nothing are tiredness, not naughtiness.',
        fix: `At about ${window - 10} minutes awake: toilet trip, then his crate with a chew, before he gets silly.`,
        goals: ['calm', 'manners', 'quiet'],
        sources: [...FIGURES.awakeWindow.sources],
        route: '/training/help?b=wont-settle',
        score: 78 + spells * 2,
      });
  }

  // 4. Long, wild play.
  {
    const long = last(3 * DAY).filter((e) => e.type === 'play' && e.end !== undefined && e.end - e.at > 25 * MINUTE);
    if (long.length >= 2)
      add({
        id: 'long-play',
        level: 'medium',
        title: 'Play sessions running long',
        evidence: `${plural(long.length, 'game')} over 25 minutes in the last three days.`,
        why: 'Long, exciting games wind a young puppy up rather than tire him out, and that tips into mouthing and zoomies.',
        fix: 'Five to ten minutes, then something calm: a sniff mat, a chew, or a nap.',
        goals: ['calm'],
        sources: [...FIGURES.awakeWindow.sources],
        route: '/training/games',
        score: 55,
      });
  }

  // 5. Socialising gone quiet in the window.
  if (ageWeeks < 16 && daysLogged >= 3) {
    const lastSocial = recent.filter((e) => e.type === 'social').reduce((m, e) => Math.max(m, e.at), 0);
    const quietDays = Math.floor((now - Math.max(lastSocial, x.homeAt ?? 0, now - 7 * DAY)) / DAY);
    if (quietDays >= 3)
      add({
        id: 'social-stalled',
        level: 'high',
        title: `No new experiences for ${quietDays >= 7 ? 'a week' : `${quietDays} days`}`,
        evidence: `About ${Math.max(0, 16 - Math.floor(ageWeeks))} weeks left of the window when new things are easiest for him.`,
        why: 'What he meets calmly now (people, noises, traffic, other dogs) he’ll take in his stride as an adult. Missed now, it can mean fear and barking later.',
        fix: 'One short, happy new thing today. Watching the street from your arms, or a new sound with treats, counts.',
        goals: ['sociable', 'quiet', 'calm'],
        sources: [...FIGURES.socialWindow.sources],
        route: '/social',
        score: 88,
      });
  }

  // 6. Going too fast with new things.
  {
    const social = last(4 * DAY).filter((e): e is LogEvent<'social'> => e.type === 'social');
    const worried = social.filter((e) => e.data.reaction === 'worried');
    if (worried.length >= 2)
      add({
        id: 'social-too-fast',
        level: worried.length >= 3 ? 'high' : 'medium',
        title: 'Some new things are worrying him',
        evidence: `${worried.length} of ${plural(social.length, 'new experience')} in the last few days left him worried.`,
        why: 'A frightening experience can teach fear instead of confidence. Socialising only works if he stays happy.',
        fix: 'Go back a step: more distance, lower volume, shorter, treats throughout, and let him choose to approach.',
        goals: ['sociable', 'quiet', 'calm'],
        sources: cite('dtSocialisation', 'rkcSocial'),
        route: '/social',
        score: 76 + worried.length,
      });
  }

  // 7. Alone time: never practised, or too long.
  {
    const alone = recent.filter((e) => e.type === 'alone');
    const limit = aloneAlertMinutes(ageWeeks);
    // Finished ones only: one still going is the live "on his own" alert's job.
    const tooLong = alone.filter((e) => e.at > since(5 * DAY) && e.end !== undefined && e.end - e.at > limit * MINUTE);
    if (tooLong.length)
      add({
        id: 'alone-too-long',
        level: 'high',
        title: 'Left on his own longer than he’s ready for',
        evidence: `${plural(tooLong.length, 'time')} over ${limit} minutes in the last five days.`,
        why: 'Long stretches alone before he’s built up to them are how separation distress, crying and barking start.',
        fix: `Keep it under ${limit} minutes for now, build up slowly, and get a friend or sitter in for longer days.`,
        goals: ['comfortable', 'quiet'],
        sources: cite('rspcaAlone', 'dtSeparation'),
        route: '/training/alone',
        score: 86,
      });
    else if (ageWeeks < 20 && daysLogged >= 4 && !alone.some((e) => e.at > since(5 * DAY)))
      add({
        id: 'no-alone',
        level: 'medium',
        title: 'Not practising time on his own',
        evidence: 'No alone time logged for five days.',
        why: 'Puppies who are always with someone find being left much harder later, and that’s when crying and barking at the door start.',
        fix: 'A few seconds behind a baby gate or a closed door while he has a chew, a couple of times a day. Build up gradually.',
        goals: ['comfortable', 'quiet'],
        sources: cite('rspcaAlone', 'dtSeparation'),
        route: '/training/alone',
        score: 60,
      });
  }

  // 8. Telling off — spotted in what you wrote.
  {
    const said = recent.filter((e) => e.note && TELLING_OFF.test(e.note));
    if (said.length)
      add({
        id: 'telling-off',
        level: 'high',
        title: 'Telling him off',
        evidence: `${said.length === 1 ? 'A note' : `${said.length} notes`} from ${formatDate(said[said.length - 1].at, 'weekday')} mention${said.length === 1 ? 's' : ''} telling him off or raising voices.`,
        why: 'Puppies can’t connect a telling-off to what they did. It makes them anxious, and anxious puppies bite, bark and hide accidents more.',
        fix: 'Interrupt calmly (a cheerful "oops!"), redirect to what you want, and reward that. For accidents: no reaction, clean with enzyme cleaner.',
        goals: ['calm', 'comfortable', 'manners'],
        sources: [...FIGURES.rewardBased.sources],
        route: '/training/help?b=bitey',
        score: 95,
      });
  }

  // 9. Training gone quiet.
  {
    const lastTraining = recent.filter((e) => e.type === 'training').reduce((m, e) => Math.max(m, e.at), 0);
    const gap = now - Math.max(lastTraining, x.homeAt ?? 0, now - 7 * DAY);
    if (daysLogged >= 3 && gap >= 2 * DAY)
      add({
        id: 'training-gap',
        level: gap >= 4 * DAY ? 'high' : 'medium',
        title: `No training for ${gap >= 7 * DAY ? 'a week' : `${Math.floor(gap / DAY)} days`}`,
        evidence: lastTraining ? `Last session ${formatDate(lastTraining, 'weekday')}.` : 'No sessions logged yet.',
        why: 'Little and often is how puppies learn. Gaps mean starting over, and the habits he practises on his own (jumping, nipping) take hold instead.',
        fix: 'Two minutes before his next meal: name game or sit, with his kibble as rewards.',
        goals: ['manners', 'quiet'],
        sources: [...FIGURES.rewardBased.sources],
        route: '/training',
        score: 58 + Math.floor(gap / DAY) * 3,
      });
  }

  // A toilet routine that's drifted: trips much further apart than he can manage.
  {
    const interval = toiletIntervalMinutes(ageWeeks);
    const day = last(2 * DAY).filter(isToilet).filter((e) => !inClockWindow(e.at, night.start, night.end)).sort((a, b) => a.at - b.at);
    const asleepBetween = (a: Millis, b: Millis) => sleepIntervals(events, a, b, now).reduce((n, [s, t]) => n + (t - s), 0);
    const gaps = day.slice(1).map((e, i) => (e.at - day[i].at - asleepBetween(day[i].at, e.at)) / MINUTE);
    const stretched = gaps.filter((g) => g > interval * 1.75);
    const inside = day.filter((e) => e.data.where === 'inside').length;
    if (gaps.length >= 6 && stretched.length >= 3 && inside >= 2)
      add({
        id: 'trips-too-far-apart',
        level: 'high',
        title: 'Toilet trips drifting too far apart',
        evidence: `${stretched.length} gaps of more than ${Math.round((interval * 1.75) / 5) * 5} awake minutes in two days, and ${plural(inside, 'accident')}.`,
        why: `At ${Math.floor(ageWeeks)} weeks he can hold on for about ${interval} minutes awake. Longer, and accidents are the routine's fault, not his.`,
        fix: 'Go back to a trip every hour he’s awake for a few days, as well as after sleep, food and play.',
        goals: ['manners'],
        sources: [...FIGURES.toiletInterval.sources],
        route: '/insights',
        score: 84,
      });
  }

  // 11. Late nights: bed (and dinner) drifting well past bedtime.
  {
    const late: { bed: Millis; dinner?: Millis }[] = [];
    let nights = 0;
    for (let i = 1; i <= 4; i++) {
      const evening = atClock(dayStart(now, -i), night.start);
      if (evening < (x.homeAt ?? 0)) break;
      // Bed: the first proper sleep from around bedtime (not a short evening nap).
      const bed = events
        .filter((e) => e.type === 'sleep' && e.at >= evening - HOUR && e.at < evening + 4 * HOUR && (e.end ?? now) - e.at >= 90 * MINUTE)
        .sort((a, b) => a.at - b.at)[0];
      if (!bed) continue;
      nights++;
      const dinner = events.filter((e) => e.type === 'meal' && e.at > evening - 5 * HOUR && e.at < bed.at).reduce<Millis | undefined>((m, e) => (m === undefined || e.at > m ? e.at : m), undefined);
      if (bed.at - evening >= 45 * MINUTE) late.push({ bed: bed.at, dinner });
    }
    if (nights >= 3 && late.length >= 2) {
      const lateDinners = late.filter((n) => n.dinner !== undefined && n.bed - n.dinner < LATE_DINNER).length;
      add({
        id: 'late-nights',
        level: 'medium',
        title: 'Late nights',
        evidence: `In bed 45+ minutes after ${night.start} on ${late.length} of the last ${nights} nights (${late.map((n) => formatTime(n.bed)).join(', ')})${lateDinners ? `, ${lateDinners === 1 ? 'once' : `${lateDinners} times`} with dinner less than 90 minutes before` : ''}.`,
        why: 'A late dinner or game means a full tummy and a wound-up puppy at bedtime: harder to settle, more night wakes and accidents. Puppies settle best on the same routine every night.',
        fix: `Last meal about two hours before bed, then a calm wind-down (chew, sniffing, cuddles, no rough play), then a last wee and into bed by ${night.start}.`,
        goals: ['calm', 'comfortable', 'manners'],
        sources: cite('blueCrossNight', 'pdsaNightCry', 'akcToilet', 'kciToilet'),
        score: 62 + late.length * 2,
      });
    }
  }

  return out.sort((a, b) => (a.level === b.level ? b.score - a.score : a.level === 'high' ? -1 : 1));
}
